import React, { useEffect, useId, useRef } from 'react';
import { X } from 'lucide-react';
import { useCloseWatcher } from '../hooks/useCloseWatcher';

/**
 * 열린 모달 스택 (모듈 스코프). ESC는 맨 위 모달만 닫는다 —
 * 예전엔 각 모달이 document 버블 단계에서 ESC를 들어 중첩 모달(관리자 콘솔 위 확인창 등)이 한꺼번에 닫혔다.
 */
const modalStack: string[] = [];

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function focusables(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    el => el.offsetParent !== null || el === document.activeElement
  );
}

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  closeOnOverlay?: boolean;
  /**
   * 본문 안에 자체 스크롤 영역(긴 목록 등)이 있을 때 true.
   * 본문이 세로 flex 컨테이너가 되어 목록이 남는 높이를 가져가고, 본문 자체는 스크롤하지 않는다.
   * 기본값(false)으로 두면 본문(60vh)과 내부 목록이 각각 스크롤돼 이중 스크롤이 생긴다.
   */
  bodyFlex?: boolean;
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  closeOnOverlay = true,
  bodyFlex = false,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const modalId = useId();
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // 모바일 뒤로가기는 맨 위 모달만 닫는다(watcher 생성 순서 = modalStack 순서).
  // closeOnOverlay={false}여도 닫는다 — 바깥 터치와 달리 뒤로가기는 의도된 조작이고 X 버튼과 같은 결과다
  useCloseWatcher(isOpen, onClose);

  // 스택 등록 + ESC(맨 위 모달만) + 포커스 저장/초기 포커스/복원 + Tab 순환 + body 스크롤 잠금
  useEffect(() => {
    if (!isOpen) return;
    modalStack.push(modalId);
    const previouslyFocused = document.activeElement as HTMLElement | null;
    document.body.style.overflow = 'hidden';

    const isTop = () => modalStack[modalStack.length - 1] === modalId;

    const onKeyDown = (e: KeyboardEvent) => {
      if (!isTop()) return;
      if (e.key === 'Escape') {
        e.stopImmediatePropagation();
        e.preventDefault();
        onCloseRef.current();
        return;
      }
      if (e.key === 'Tab' && modalRef.current) {
        const list = focusables(modalRef.current);
        if (list.length === 0) {
          e.preventDefault();
          modalRef.current.focus();
          return;
        }
        const first = list[0];
        const last = list[list.length - 1];
        const active = document.activeElement as HTMLElement | null;
        const inside = !!active && modalRef.current.contains(active);
        if (e.shiftKey) {
          if (!inside || active === first || active === modalRef.current) {
            e.preventDefault();
            last.focus();
          }
        } else if (!inside || active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    // capture 단계: 배경(채팅 검색 Ctrl+F·ESC 등)보다 먼저 받아 새어 나가지 않게 한다 (HelpPage 패턴)
    window.addEventListener('keydown', onKeyDown, true);

    // 초기 포커스: 첫 focusable(없으면 컨테이너). 조건부 렌더 직후라 다음 프레임에
    const raf = requestAnimationFrame(() => {
      const root = modalRef.current;
      if (!root) return;
      const list = focusables(root);
      (list[0] ?? root).focus();
    });

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('keydown', onKeyDown, true);
      const idx = modalStack.lastIndexOf(modalId);
      if (idx >= 0) modalStack.splice(idx, 1);
      if (modalStack.length === 0) document.body.style.overflow = '';
      // 열기 전 포커스 복원 (트리거 버튼으로 되돌아간다). 그 요소가 사라졌으면 그대로 둔다
      if (previouslyFocused && document.contains(previouslyFocused) && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus();
      }
    };
  }, [isOpen, modalId]);

  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* 오버레이 */}
      <div
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
        onClick={closeOnOverlay ? onClose : undefined}
      />

      {/* 모달 컨텐츠 — max-h + flex-col: 작은 폰·키보드 열림·긴 본문에서 패널이 뷰포트를 넘으면 본문만 줄어
          스크롤되고 헤더/푸터(저장 버튼)는 항상 보인다. 래퍼가 items-center라 max-h가 없으면 위아래가 잘린 채 복구 불가.
          기준이 dvh가 아니라 %인 이유: iOS는 키보드가 떠도 dvh가 줄지 않는다 — 키보드가 뜨면
          useSoftKeyboardViewportFix가 #root를 보이는 영역 크기로 줄이므로, 부모(fixed inset-0) 높이 기준인
          %만이 키보드 위 영역에 맞게 따라 줄어든다(평상시엔 100% = 100dvh로 동일) */}
      <div
        ref={modalRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={`relative w-full ${sizeClasses[size]} mx-4 max-h-[calc(100%-2rem)] flex flex-col bg-white rounded-2xl shadow-xl animate-modal-enter focus:outline-none`}
      >
        {/* 헤더 */}
        <div className="shrink-0 flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h2 id={titleId} className="text-lg font-semibold text-slate-800">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="닫기"
            className="p-2 -mr-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X size={20} aria-hidden="true" />
          </button>
        </div>

        {/* 본문 */}
        <div
          className={`px-6 py-4 min-h-0 ${
            bodyFlex
              ? 'max-h-[75vh] flex flex-col'
              : 'max-h-[60vh] overflow-y-auto'
          }`}
        >
          {children}
        </div>

        {/* 푸터 */}
        {footer && (
          <div className="shrink-0 px-6 py-4 border-t border-slate-100 bg-slate-50 rounded-b-2xl">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};
